/**
 * PartnerPicker — inline-выбор партнёра в стиле Linear/Notion.
 *
 * В свёрнутом виде показывает имя партнёра (или placeholder).
 * По клику открывается Popover с:
 *  - search-input сверху (debounced server-side поиск через partnerRepository.search);
 *  - inline-списком партнёров с name + hint (ИНН);
 *  - первой строкой "Не указан" (для null);
 *  - чекмарком на текущем выбранном.
 *
 * При canEdit=false — display рендерится как Anchor с onClick→onPartnerClick
 * (чтобы сохранить навигацию к карточке партнёра).
 *
 * Готовый блок для полей "Партнёр", "Контрагент" в карточках лида/сделки и т.п.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Anchor,
  Avatar,
  Box,
  Group,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  type MantineSize,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCheck } from '@tabler/icons-react';
import { InlinePicker } from '@ayocore/platform/client/components/InlinePicker';
import { pickAvatarInitial } from '@ayocore/platform/client/utils';
import { usePartnerRepository } from '@ayocore/erp/crm/client/rpc';
import type { Partner } from '../../../schemas/partner.schema';

export interface PartnerPickerProps {
  /** ID выбранного партнёра. */
  value: number | null;
  /** Колбек с новым ID (или null при очистке). */
  onChange: (partnerId: number | null) => void;
  /**
   * Объект партнёра для рендера display (имя). Прокидывается из родителя
   * (lead.partner, deal.partner и т.д.).
   */
  partner?: {
    id: number;
    name?: string | null;
    full_name?: string | null;
    inn?: string | null;
  } | null;
  /** Плейсхолдер когда partner пустой. По умолчанию "Не указан". */
  placeholder?: string;
  /** Если false — display только, без popover (Anchor при наличии partner). */
  canEdit?: boolean;
  /** Колбек клика по имени партнёра (для навигации к карточке). */
  onPartnerClick?: (partnerId: number) => void;
  /** Размер свёрнутого display. По умолчанию 'xs'. */
  size?: MantineSize;
  /** Размер содержимого popover. По умолчанию 'sm'. */
  inputSize?: MantineSize;
}

type PartnerOptionRowProps = {
  label: string;
  hint?: string | null;
  /** Если true — серая "пустая" иконка вместо обычной (для строки "Не указан"). */
  emptyIcon?: boolean;
  selected: boolean;
  onClick: () => void;
  size: MantineSize;
};

function PartnerOptionRow({
  label,
  hint,
  emptyIcon,
  selected,
  onClick,
  size,
}: PartnerOptionRowProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        padding: '6px 8px',
        borderRadius: 4,
        background: hovered ? 'var(--mantine-color-gray-1)' : 'transparent',
        transition: 'background 0.1s ease',
      }}
    >
      <Group gap={8} wrap='nowrap' justify='space-between'>
        <Group gap={8} wrap='nowrap' style={{ minWidth: 0 }}>
          <Avatar size={size} radius='xl' color={emptyIcon ? 'gray' : 'violet'}>
            {emptyIcon ? null : pickAvatarInitial(label)}
          </Avatar>
          <Box style={{ minWidth: 0 }}>
            <Text
              size={size}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {label}
            </Text>
            {hint ? (
              <Text
                size='xs'
                c='dimmed'
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {hint}
              </Text>
            ) : null}
          </Box>
        </Group>
        {selected ? <IconCheck size={14} color='var(--mantine-color-blue-6)' /> : null}
      </Group>
    </Box>
  );
}

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 15;

export function PartnerPicker({
  value,
  onChange,
  partner,
  placeholder = 'Не указан',
  canEdit = true,
  onPartnerClick,
  size = 'sm',
  inputSize = 'sm',
}: PartnerPickerProps) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const partnerRepository = usePartnerRepository();

  // Загрузка списка: при первом открытии (top-N) и при изменении debounced-запроса.
  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const query = debouncedSearch.trim();
        const rows = query
          ? await partnerRepository.search(query).limit(SEARCH_LIMIT).fetch()
          : await partnerRepository.orderBy('name', 'asc').limit(SEARCH_LIMIT).fetch();
        if (!cancelled) setResults(rows ?? []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, debouncedSearch, partnerRepository]);

  const partnerName = useMemo(
    () => partner?.name ?? partner?.full_name ?? null,
    [partner?.name, partner?.full_name]
  );

  const selectPartner = (id: number | null) => {
    onChange(id);
    setOpened(false);
    setSearch('');
  };

  // Display: при canEdit=false и наличии партнёра — Anchor для навигации.
  const display =
    !canEdit && partner && partnerName ? (
      <Anchor
        size={size}
        onClick={() => onPartnerClick?.(partner.id)}
        style={{
          color: '#6e4be2',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {partnerName}
      </Anchor>
    ) : (
      <Text
        size={size}
        c={partnerName ? '#6e4be2' : 'dimmed'}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {partnerName ?? placeholder}
      </Text>
    );

  return (
    <InlinePicker
      display={display}
      opened={opened}
      onOpenChange={setOpened}
      canEdit={canEdit}
      width={320}
    >
      <Stack gap={6}>
        <TextInput
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          placeholder='Поиск партнёра...'
          size={inputSize}
          autoFocus
        />
        <ScrollArea.Autosize mah={320} type='hover' scrollbarSize={6} scrollbars='y'>
          <Stack gap={2}>
            <PartnerOptionRow
              label='Не указан'
              emptyIcon
              selected={value === null}
              onClick={() => selectPartner(null)}
              size={inputSize}
            />
            {results.map(p => (
              <PartnerOptionRow
                key={p.id}
                label={p.name ?? p.full_name ?? `Партнёр #${p.id}`}
                hint={p.inn ?? undefined}
                selected={p.id === value}
                onClick={() => selectPartner(p.id)}
                size={inputSize}
              />
            ))}
            {loading ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                Загрузка...
              </Text>
            ) : null}
            {!loading && results.length === 0 && search ? (
              <Text size='xs' c='dimmed' ta='center' py='xs'>
                Никого не найдено
              </Text>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
      </Stack>
    </InlinePicker>
  );
}
